// Simple state management
import type { ProfilePhoto } from './types/api';
import type { ProfileJson, FiltersJson, VisiJson } from './options';

export interface BrowseContext {
  profiles: { user_id: string; distance_km?: number }[];
  currentIndex: number;
  scrollPosition?: number;
  returnTo?: string; // The route to return to (e.g., '/browse')
}

export interface User {
  id: string;
  email: string;
  phone_number: string | null;
  email_verified: boolean;
  profile: Profile | null;
  preferences: Preferences | null;
}

export interface Profile {
  user_id: string;
  first_name: string | null;
  birth_date: string | null;
  gender_id: number | null;
  gender?: string | null;
  relationship_status_id: number | null;
  relationship_status?: string | null;
  bio: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  school: string | null;
  company: string | null;
  job_title: string | null;
  height_cm: number | null;
  handle?: string | null;
  profile_json?: ProfileJson | null;
}

export interface Preferences {
  user_id: string;
  age_min: number;
  age_max: number;
  distance_max_km: number;
  live_typing_enabled: boolean;
  show_mature_content: boolean;
  show_explicit_content: boolean;
  discoverable?: boolean;
  filters_json?: FiltersJson;
  visi_json?: VisiJson;
}

export interface Gender {
  id: number;
  name: string;
}

export interface RelationshipStatus {
  id: number;
  name: string;
}

export interface Match {
  user_id: string;
  first_name: string;
  age: number;
  gender: string | null;
  city: string | null;
  my_score: number;
  their_score: number;
  matched_at: string;
  chat_id: string | null;
  last_message: string | null;
  last_message_at: string | null;
  unread_count: number;
  avatar_url: string | null;
  distance_km: number | null;
  message_count: number;
  qa_answer_count: number;
}

export interface Chat {
  id: string;
  other_user_id: string;
  other_user_name: string;
  last_message: string | null;
  last_message_at: string | null;
  unread_count: number;
}

export interface MessageImage {
  media_id: string;
  thumb_url: string;
  large_url: string;
  sort_order: number;
}

export interface PendingImageInfo {
  media_id: string;
  caption_ai: string | null;
}

export interface Message {
  id: string;
  chat_id: string;
  sender_id: string;
  content: string | null;
  read_at: string | null;
  created_at: string;
  moderation_flagged: boolean | null;
  delivery_status?: 'delivered' | 'pending_approval' | 'rejected';
  images?: MessageImage[];
  pending_image_info?: PendingImageInfo[];
  moderation_summary?: Record<string, number> | null;
}

interface AppState {
  user: User | null;
  genders: Gender[];
  relationshipStatuses: RelationshipStatus[];
  accessToken: string | null;
  refreshToken: string | null;
  currentView: string;
}

type Listener = () => void;

class Store {
  private state: AppState = {
    user: null,
    genders: [],
    relationshipStatuses: [],
    accessToken: null,
    refreshToken: null,
    currentView: 'login',
  };

  private listeners: Listener[] = [];
  private browseContext: BrowseContext | null = null;
  private distanceCache = new Map<string, number>();

  constructor() {
    // Load tokens from localStorage
    this.state.accessToken = localStorage.getItem('accessToken');
    this.state.refreshToken = localStorage.getItem('refreshToken');
  }

  getState(): AppState {
    return this.state;
  }

  setState(partial: Partial<AppState>): void {
    this.state = { ...this.state, ...partial };
    this.notify();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notify(): void {
    this.listeners.forEach(l => l());
  }

  // Auth methods
  setTokens(accessToken: string, refreshToken: string): void {
    this.state.accessToken = accessToken;
    this.state.refreshToken = refreshToken;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    this.notify();
  }

  clearTokens(): void {
    this.state.accessToken = null;
    this.state.refreshToken = null;
    this.state.user = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    this.notify();
  }

  isAuthenticated(): boolean {
    return !!this.state.accessToken;
  }

  setUser(user: User): void {
    this.state.user = user;
    this.notify();
  }

  setGenders(genders: Gender[]): void {
    this.state.genders = genders;
    this.notify();
  }

  setRelationshipStatuses(statuses: RelationshipStatus[]): void {
    this.state.relationshipStatuses = statuses;
    this.notify();
  }

  setCurrentView(view: string): void {
    this.state.currentView = view;
    this.notify();
  }

  // Browse context methods (doesn't trigger re-render)
  setBrowseContext(context: BrowseContext | null): void {
    this.browseContext = context;
  }

  getBrowseContext(): BrowseContext | null {
    return this.browseContext;
  }

  // Distance cache (populated by browse and matches views)
  setDistance(userId: string, distanceKm: number): void {
    this.distanceCache.set(userId, distanceKm);
  }

  getDistance(userId: string): number | undefined {
    return this.distanceCache.get(userId);
  }

  updatePreferences(prefs: Partial<Preferences>): void {
    if (!this.state.user) return;
    this.state.user = {
      ...this.state.user,
      preferences: { ...this.state.user.preferences!, ...prefs },
    };
    this.notify();
  }
}

export const store = new Store();
